import React, { useEffect, useState } from "react"
import { Box, FormHelperText, Typography } from "@mui/material"
import { AppFilterChip } from "@components"
import { dsColorNew } from "@palette"
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"
import { useSiteCatalog } from "Pages/DsRepricing/Setup/@services/sites"
import { useProductById } from "Pages/DsRepricing/Setup/@services/useProducts"
import { productSites } from "Pages/DsRepricing/Setup/@services/products"
import { fitSiteScope, convertedLabel, SITES_EMPTIED_ERROR } from "Pages/DsRepricing/Page/@services/siteScope"

// The site scope of a strategy lists ONLY the sites of the chosen product
// (CU-869fhuhy6, brief v3): a product is configured per site, so a site
// the product is not sold on has nothing to read or write. Nothing
// selected = every site of the product. Stored in `filterGroupSites`
// (plain ids; `toServer` extracts ids either way), inclusion only: the
// exclusion switch has no meaning over a short list. A site the product
// does not have is pruned AND SAID (criterion 10, UX v3 D20): one line
// under the chips for as long as the drawer is open. An older strategy
// saved as an exclusion ("every site but Y") is converted to the equivalent
// list (the product's sites minus Y, never Y) and that is said too. An
// exclusion of every site of the product leaves nothing: saving is blocked
// until a site is picked, because empty would mean every site.
const idsOf = (list) => (Array.isArray(list) ? list.map(o => Number(o?.value ?? o)).filter(Number.isFinite) : [])

export default function DsRepricingProductSites() {
	const sites = useSiteCatalog()
	const d = dsRepricingDrawerData.value || {}
	const product = useProductById(d.productId)
	const allowed = productSites(product)
	const selected = idsOf(d.filterGroupSites)
	const [pruned, setPruned] = useState(null)   // { ids, productName } — what the last product change removed
	const [converted, setConverted] = useState(null)   // { ids, emptied, productId } — the excluded sites of an older strategy, now a list
	// The converted exclusion left no site of this product and none is picked yet: empty would read "every site".
	const mustPick = !!(converted?.emptied && String(converted.productId) === String(d.productId) && selected.length === 0)

	// A site that left the product (another product picked, a row removed) leaves the scope too — and the
	// line below says so. Without a product nothing is pruned: a legacy strategy keeps its sites until one is picked.
	useEffect(() => {
		if (!product) return
		const fit = fitSiteScope(selected, !!d.filterGroupSitesExclusion, allowed)
		if (fit.changed) {
			dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, filterGroupSites: fit.sites, filterGroupSitesExclusion: false }
			if (fit.gone.length) setPruned({ ids: fit.gone, productName: product.name })
			if (fit.converted) setConverted({ ids: fit.converted, emptied: fit.emptied, productId: d.productId })
		}
	}, [allowed.join(','), d.productId])

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, filterGroupSites: !mustPick }
	}, [mustPick])
	useEffect(() => () => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.peek(), filterGroupSites: undefined }
	}, [])

	const toggle = (id) => {
		const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
		dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, filterGroupSites: next, filterGroupSitesExclusion: false }
	}

	if (!product) {
		return (
			<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, fontStyle: 'italic' }}>
				Pick a product in Basic (card 2) — its sites appear here. A strategy can only be compared on the sites its product is sold on.
			</Typography>
		)
	}
	return (
		<Box>
			<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, fontWeight: 600, mb: 1 }}>Sites of {product.name}</Typography>
			{/* The app's filter chips (soft blue while on), one per site of the product */}
			<Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
				{allowed.map(id => (
					<AppFilterChip key={id} icon="store" label={sites.labelOf(id)} selected={selected.includes(id)} onClick={() => toggle(id)}/>
				))}
				{allowed.length === 0 && <Typography sx={{ fontSize: 13, color: dsColorNew.noteAmber }}>This product has no row with a site yet — add one in Setup → Products.</Typography>}
			</Box>
			{pruned && (
				<Typography sx={{ display: 'block', mt: 1, fontSize: 12, color: dsColorNew.noteAmber, fontWeight: 600 }}>
					Removed from this strategy: {pruned.ids.map(sites.labelOf).join(', ')} — not a site of "{pruned.productName}".
				</Typography>
			)}
			{converted && (
				<Typography sx={{ display: 'block', mt: 1, fontSize: 12, color: dsColorNew.noteAmber, fontWeight: 600 }}>
					{converted.emptied
						? `Converted "${convertedLabel(converted.ids, sites.labelOf)}" to a list: no site of the product is left.`
						: `Converted "${convertedLabel(converted.ids, sites.labelOf)}" to the equivalent list.`}
				</Typography>
			)}
			{mustPick
				? <FormHelperText error sx={{ mt: 1 }}>{SITES_EMPTIED_ERROR}</FormHelperText>
				: <Typography component="span" sx={{ display: 'block', mt: 1, fontSize: 12, color: dsColorNew.textMuted }}>
				Only the sites of the product can be chosen. {selected.length === 0
					? `None selected = all ${allowed.length} site${allowed.length === 1 ? '' : 's'} of the product.`
					: `${selected.length} of ${allowed.length} selected.`}
			</Typography>}
		</Box>
	)
}
